import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GraphQLModule, PlusAuthenticationService } from '@karve.it/core';
import { uniq } from 'lodash';
import MiniSearch from 'minisearch';
import { TreeNode } from 'primeng/api';
import { TreeFilterEvent } from 'primeng/tree';
import { TreeSelect } from 'primeng/treeselect';
import { distinctUntilChanged } from 'rxjs/operators';
import { ZONE_FILTER_THRESHOLD } from 'src/app/global.constants';
import { ZoneTreeBranch, generateZoneTree } from 'src/app/utilities/zones.util';
import { SubSink } from 'subsink';

import { AvailableZoneFragment, BaseRoleFragment } from '../../../generated/graphql.generated';
import { AppMainComponent } from '../../app.main.component';
import { AvailableZonesService } from '../../services/available-zones.service';
import { BrandingService } from '../../services/branding.service';

interface TreeNodeData {
  zone: AvailableZoneFragment;
  role: BaseRoleFragment;
}

@Component({
  selector: 'app-zone-select',
  templateUrl: './zone-select.component.html',
  styleUrls: ['./zone-select.component.scss']
})
export class ZoneSelectComponent implements OnInit, OnDestroy, AfterViewInit {
  subs = new SubSink();

  miniSearch = new MiniSearch({
    fields: ['label'],
    idField: 'key',
    searchOptions: {
      fuzzy: 0.2,
      prefix: true,
    },
  });

  zoneTree: TreeNode<TreeNodeData>[] = [];

  // Filter functionality
  filteredNodes: TreeNode<TreeNodeData>[] = [];

  selectedZone: TreeNode<TreeNodeData>;
  showFilter = false;

  @ViewChild('treeSelect', { static: true }) treeSelect: TreeSelect;

  get currentZone() {
    return this.branding.currentZone().value;
  }

  get currentRoute() {
    return this.router.url;
  }

  constructor(
    public availableZonesService: AvailableZonesService,
    private plusAuth: PlusAuthenticationService,
    public branding: BrandingService,
    private router: Router,
    public graphqlModule: GraphQLModule,
    private appMain: AppMainComponent,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.subs.sink = this.availableZonesService.availableZones
    .subscribe(async () => {

      this.zoneTree = this.getAvailableZoneTree();

      this.selectCurrentZoneNode(this.zoneTree);
      this.determineIfFilterIsRequired();

      // Call detect changes on appMain to prevent an `ExpressionChangedAfterItHasBeenCheckedError`
      this.appMain.cd.detectChanges();
    });

    // Keep the displayed selection in sync with the context that will actually
    // be used for requests. Watches zone only — role is resolved server-side.
    this.subs.sink = this.graphqlModule.zone
    .pipe(distinctUntilChanged())
    .subscribe(() => {
      this.selectCurrentZoneNode(this.zoneTree);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent) {
    // Ctrl + Alt + z
    if (event.ctrlKey && event.altKey && event.key === 'z' && this.treeSelect) {
      event.preventDefault();
      this.treeSelect.focusInput.nativeElement.click();
    }
  }

  onZoneSelected(evnt: { node: any }) {
    const { zone } = evnt.node.data;
    this.treeSelect.hide(evnt);

    // If the user is on a specific job page, redirect to the jobs list
    // after changing the zone — the job's data is scoped to its own zone.
    const pathname = this.router.url.split('?')[0].split('#')[0];
    const isOnJobPage = /^\/jobs\/(?!new(?:\/|$))[^/]+(?:\/.*)?$/.test(pathname);

    this.plusAuth.setContext(zone.id)
    .then(() => {
      if (isOnJobPage) {
        this.router.navigate(['/jobs']);
      }
    })
    .catch((err) => {
      console.error('Error setting zone', err);
    });
  }

  /**
   * From the provided zoneTree, sets the currently contexted zone as the
   * `selectedZone` and expands it and its children
   *
   * @param zoneTree zoneTree calcualted from convertZoneTreeBranchToTreeNode
   * @returns true if this or a child branch was selected
   */
  selectCurrentZoneNode(zoneTree: TreeNode<TreeNodeData>[]) {
    if (!zoneTree) { return; }

    for (const branch of zoneTree) {
      const isZone = branch.data?.zone?.id === this.graphqlModule.zone.value;
      if (isZone) {
        branch.expanded = true;
        this.selectedZone = branch;
        return true;
      } else if (branch.children) {
        const selected = this.selectCurrentZoneNode(branch.children);
        if (selected) {
          branch.expanded = true;
          return true;
        }
      }
    }

    return false;
  }

  /**
   * converts  zoneTreeBranch array structure from generateZoneTree
   * into primeng TreeNode objects.
   */
  convertZoneTreeBranchToTreeNode(availableZoneTree: ZoneTreeBranch<AvailableZoneFragment>[]) {
    const _zoneTree: TreeNode<TreeNodeData>[] = [];

    availableZoneTree = availableZoneTree.sort((a, b) => {

      if (a.children.length && !b.children.length) {
        return -1;
      }

      return a.zone.name.localeCompare(b.zone.name);
    });

    for (const branch of availableZoneTree) {
      const zone = branch.zone;

      const treeNode: TreeNode<TreeNodeData> = {
        key: zone.id,

        label: zone.name,
        data: {
          zone: branch.zone,
          role: zone.contextableRoles?.[0] as any,
        },
        children: this.convertZoneTreeBranchToTreeNode(branch.children),
      };

      _zoneTree.push(treeNode);
    }

    return _zoneTree;
  }

  /**
   * Count the available zones in the zone tree.
   * Used to determine if we want to show a searchbox or not
   *
   * Right now the searchbox is not enabled so this is not really used.
   *
   * @param zoneTree
   * @returns
   */
  countAvailableZones(zoneTree: TreeNode[]) {
    if (!zoneTree) { return 0; }
    let count = zoneTree.length;
    for (const branch of zoneTree) {
      if (branch.children) {
        count += this.countAvailableZones(branch.children);
      }
    }

    return count;
  }

  /**
   * Set showFilter to true if the tree node is complex enough
   */
  determineIfFilterIsRequired() {
    const count = this.countAvailableZones(this.zoneTree);
    this.showFilter = count > ZONE_FILTER_THRESHOLD;
  }

  /**
   * Converts an array of zones into the tree structure expected by the TreeSelect component.
   * Along the way, fills out `this.availableZoneMap` to make it easier to set `this.currentZone` on zone change.
   *
   * @param zones An array of zones.
   * @returns A tree structure where each node holds a zone.
   */
  getAvailableZoneTree(): TreeNode<TreeNodeData>[] {
    const availableZones = this.availableZonesService.availableZones.value;
    if (!availableZones) {
      // available zones should be set
      return;
    }

    const availableZoneTree = generateZoneTree(availableZones);
    const zoneTree = this.convertZoneTreeBranchToTreeNode(availableZoneTree);
    // console.log({availableZoneTree, zoneTree});

    // expand top nodes
    for (const branch of zoneTree) {
      branch.expanded = true;
    }

    return zoneTree;
  }

  updateFilterModel(filterEvent?: TreeFilterEvent) {
    this.filteredNodes = filterEvent?.filteredValue || [];
  }

  resetFilterModel() {
    this.filteredNodes = [];
  }

  flattenNodes(treeNodes: TreeNode<TreeNodeData>[]) {
    const result: TreeNode<TreeNodeData>[] = [];

    for (const node of treeNodes) {
      result.push(node);

      if (node.children && node.children.length > 0) {
        result.push(...this.flattenNodes(node.children));
      }
    }

    return result;
  }

  /**
   * Finds and selects the closest matching zone based on the current filter using MiniSearch
   */
  selectClosestMatch(query?: string) {

    if (!query) return;

    query = query.trim();

    if (query.length === 0) return; 

    this.miniSearch.removeAll();

    this.miniSearch.addAll(uniq(this.flattenNodes(this.filteredNodes)));

    const searchResults = this.miniSearch.search(query);

    const [ firstMatch ] = searchResults;

    if (!firstMatch) return; 

    const [ zoneId ] = firstMatch.id.split(":");

    this.treeSelect.hide();

    this.plusAuth.setContext(zoneId)
      .catch((err) => console.error('Error setting zone', err));
  }

}
